/**
 * State Machine Definitions for Three-Axis Order Status
 *
 * Defines valid transitions for each axis and provides validation functions.
 * This is the single source of truth for state transitions — used by:
 * 1. Server-side services (packages/core)
 * 2. API route validation (apps/api)
 * 3. Firebase trigger validation (apps/functions)
 * 4. POS client-side validation (Dart port in apps/pos)
 */

import type { CheckState, KitchenState, KitchenStatus, OrderState, PaymentStatus } from './types';

// ─── Order State Machine ────────────────────────────────────────────────────

/**
 * Valid transitions for OrderState.
 * Key = current state, Value = set of valid next states.
 * 'closed' is never set directly — it's set by the composite close check.
 */
/**
 * Kitchen statuses that mean the kitchen HAS the line — it is on paper, on a
 * screen, or on the pass. An order carrying any of these can no longer be
 * CANCELLED; discarding it is a VOID, which carries approval and an audit
 * trail.
 *
 * 🔴 `fired` was absent from both hand-rolled copies of this list
 * (`Order.requiresVoiding` here and `order.dart:710` on the POS), so an order
 * the kitchen was actively cooking reported `canBeCancelled: true` and was
 * discarded with no approval and no trail. Production, House of Pho: order #2
 * on 2026-08-27, and #22/#23/#24 on 2026-08-26.
 *
 * Named once and exported precisely so a third copy is not written.
 */
export const KITCHEN_COMMITTED_STATUSES: readonly string[] = [
  'sentToKitchen',
  'fired',
  'inPreparation',
  'ready',
  'served',
] as const;

/**
 * KITCHEN-safety gate for an ORDER transition — a THIRD axis beside
 * {@link validateOrderStateTransition} (shape) and
 * {@link validateOrderPaymentSafety} (money). Sibling of
 * {@link validateCheckPaymentSafety}. {@link validateOrderStateTransition}
 * governs SHAPE only and cannot see the items.
 *
 * Cancelling is the "nothing happened yet" path. Once the kitchen holds a
 * line, the correct action is a VOID — and the server must say so, because
 * until now this rule lived only in the POS. A rule enforced on one client is
 * one bad build away from not existing, which is exactly how order #2 was
 * cancelled with food on the pass.
 *
 * @returns null if permitted, an error message otherwise
 */
export function validateOrderKitchenSafety(
  targetState: OrderState,
  itemKitchenStatuses: readonly string[],
): string | null {
  if (targetState !== 'cancelled') return null;

  const committed = itemKitchenStatuses.filter(st =>
    KITCHEN_COMMITTED_STATUSES.includes(st),
  );
  if (committed.length === 0) return null;

  return (
    `Cannot cancel — the kitchen already has ${committed.length} line(s) ` +
    `(${[...new Set(committed)].join(', ')}). Void the order instead, which ` +
    `records who authorized it.`
  );
}

export const ORDER_STATE_TRANSITIONS: Record<OrderState, readonly OrderState[]> = {
  draft:             ['open', 'pendingAcceptance', 'scheduled', 'cancelled'],
  pendingAcceptance: ['open', 'scheduled', 'cancelled'],
  // A scheduled order that becomes DUE at a manual-acceptance restaurant moves
  // into the acceptance gate (the scheduler can't auto-fire it), so it surfaces
  // in the POS "Needs Approval" screen and the customer sees "awaiting
  // confirmation" rather than an indefinite "Confirming…".
  scheduled:         ['open', 'pendingAcceptance', 'cancelled'],
  open:              ['held', 'cancelled', 'voided', 'closed', 'transferred'],
  // `closed` is reachable from `held` for the same reason it is from `open`:
  // the composite close fires on a settled balance, and pausing a ticket does
  // not un-settle it. Leaving it out made the close the server actually
  // performs (House of Pho #38) illegal by this table's own account — and
  // `validateOrderStateTransition` is consulted by orderLifecycleService and
  // orderService, so the API close path would have rejected a held order even
  // after axis 1 was widened.
  held:              ['open', 'cancelled', 'voided', 'closed', 'transferred'],
  closed:            [],  // Terminal
  cancelled:         [],  // Terminal
  voided:            [],  // Terminal
  transferred:       [],  // Terminal: all items moved to another order
  // Checkout-held states (online persist-first). EMPTY here on purpose: the
  // GENERAL machine — every lifecycle service, kitchen path, the Functions
  // cascade and the till — must never move an order out of (or into) them.
  // An `awaitingPayment` order whose charge has not been approved is not an
  // order anyone may open, cancel or close. The one writer that moves them is
  // the checkout completer, validated by `validateCheckoutOrderTransition`
  // against CHECKOUT_ORDER_TRANSITIONS below.
  awaitingPayment:   [],
  abandoned:         [],
} as const;

/** Terminal order states — no further transitions allowed */
export const TERMINAL_ORDER_STATES: readonly OrderState[] = ['closed', 'cancelled', 'voided', 'transferred'] as const;

/**
 * Checkout-held order states: an online order persisted BEFORE its card is
 * charged (`awaitingPayment`) and one whose checkout never paid (`abandoned`).
 * docs/superpowers/specs/2026-09-30-online-order-persist-first-design.md.
 *
 * INVISIBLE and INERT to every consumer — no board, list, KDS, print, kitchen,
 * autopilot, marketplace push, notification, receipt, Books sync, loyalty,
 * report, order-number reconcile or table lookup reads them as an order. They
 * are neither live nor terminal: a reader asking "is this order active?" or
 * "is this order finished?" must answer NO to both, which is why they are kept
 * out of {@link TERMINAL_ORDER_STATES} and {@link NON_TERMINAL_ORDER_STATES}
 * alike. Use {@link isCheckoutHeldOrderState} rather than a literal list.
 */
export const CHECKOUT_HELD_ORDER_STATES: readonly OrderState[] = ['awaitingPayment', 'abandoned'] as const;

/** Is this order state checkout-held (invisible and inert)? Unknown/absent → false. */
export function isCheckoutHeldOrderState(state: unknown): boolean {
  return state === 'awaitingPayment' || state === 'abandoned';
}

/**
 * The ONLY transitions out of (and into) the checkout-held states. Used by the
 * checkout completer, never by the general machine:
 *
 *   awaitingPayment → open | scheduled | pendingAcceptance   (charge approved —
 *                     the acceptance decision's initial state, as a POS/marketplace
 *                     order would have been born)
 *   awaitingPayment → abandoned                              (no approved charge
 *                     after the abandon window — the finisher)
 *   abandoned       → awaitingPayment                        (a NEW attempt on
 *                     the same cart: the order IS its cart, so it is revived, with
 *                     a fresh number, rather than forked)
 */
export const CHECKOUT_ORDER_TRANSITIONS: Readonly<Partial<Record<OrderState, readonly OrderState[]>>> = {
  awaitingPayment: ['open', 'scheduled', 'pendingAcceptance', 'abandoned'],
  abandoned: ['awaitingPayment'],
} as const;

/**
 * Validate a checkout-completer transition.
 * @returns null if valid, an error message otherwise
 */
export function validateCheckoutOrderTransition(from: OrderState, to: OrderState): string | null {
  if (from === to) return null;
  const targets = CHECKOUT_ORDER_TRANSITIONS[from];
  if (!targets) {
    return `Order state "${from}" is not checkout-held — the checkout completer does not move it`;
  }
  if (!targets.includes(to)) {
    return `Invalid checkout transition: "${from}" → "${to}". Valid: [${targets.join(', ')}]`;
  }
  return null;
}

/**
 * Non-terminal order states — every `OrderState` except `draft` (not yet
 * committed, so never a table's "current" order) and the four
 * {@link TERMINAL_ORDER_STATES}. This is the set a table's "what's the open
 * order here" query (`getCurrentOrderForTable`, `packages/core`) matches an
 * `orderState in [...]` filter against — Guest Table Page Task 4.
 *
 * Derived, not hand-listed: `ORDER_STATE_TRANSITIONS` above is typed
 * `Record<OrderState, readonly OrderState[]>`, so TypeScript already refuses
 * to compile that object literal unless every `OrderState` member appears as
 * one of its keys — it IS the exhaustive key list this constant needs. A
 * state added to `OrderState` with no matching `ORDER_STATE_TRANSITIONS` entry
 * fails to compile there, before it could ever reach this filter unclassified
 * as open-or-closed.
 */
export const NON_TERMINAL_ORDER_STATES: readonly OrderState[] = (
  Object.keys(ORDER_STATE_TRANSITIONS) as OrderState[]
).filter(
  (state) =>
    state !== 'draft' &&
    !TERMINAL_ORDER_STATES.includes(state) &&
    // Checkout-held orders are not a table's (or anyone's) current order.
    !CHECKOUT_HELD_ORDER_STATES.includes(state),
);

/**
 * Validate an order state transition.
 * @returns null if valid, error message if invalid
 */
export function validateOrderStateTransition(
  from: OrderState,
  to: OrderState,
): string | null {
  if (from === to) {
    return null; // Idempotent: same state is always valid (no-op)
  }

  const validTargets = ORDER_STATE_TRANSITIONS[from];
  if (!validTargets) {
    return `Unknown order state: "${from}"`;
  }

  if (!validTargets.includes(to)) {
    return `Invalid order state transition: "${from}" → "${to}". Valid transitions from "${from}": [${validTargets.join(', ')}]`;
  }

  return null;
}

/**
 * Check if an order can be cancelled (pre-kitchen requirement).
 * Cancel is only allowed if no items have been committed to the kitchen.
 */
export function canCancelOrder(kitchenState: KitchenState): boolean {
  return kitchenState === 'none' || kitchenState === 'pending';
}

/**
 * Check if an order requires voiding instead of cancelling.
 * Voiding is required when food cost has been incurred (items past 'pending').
 */
export function requiresVoid(kitchenState: KitchenState): boolean {
  return ['sent', 'preparing', 'ready', 'served'].includes(kitchenState);
}

// ─── Kitchen State Machine (per-item) ───────────────────────────────────────

/**
 * Valid transitions for per-item KitchenStatus.
 * Key = current status, Value = set of valid next statuses.
 */
export const KITCHEN_STATUS_TRANSITIONS: Record<KitchenStatus, readonly KitchenStatus[]> = {
  // 'ready' is reachable directly from every status the KITCHEN HAS (sent,
  // fired, in preparation) so staff can "mark order ready" in one action — an
  // expo bumping a whole order forward. It is a forward fast-forward, NOT a
  // skipped gate: the server still stamps readyAt and derives the order-level
  // 'ready' state.
  //
  // 🔴 NOT from `pending` (D4, 2026-09-25). A pending line has never reached
  // the kitchen — no ticket printed, nothing on a KDS. Readying it told the
  // guest their food was ready when nobody had been asked to make it: the
  // till's "Order Ready", the marketplace mark-ready and the autopilot's
  // courier-handoff branch all did it. Send it first; the kitchen
  // orchestration refuses it with `ITEM_NOT_SENT_TO_KITCHEN` for every caller.
  pending:       ['sentToKitchen', 'cancelled', 'fired'],
  // `fired` is reachable from `sentToKitchen` because a HELD course is already
  // with the kitchen: send commits every line (owner decision 4, nothing is
  // withheld), and firing RELEASES a course the line is already holding on
  // paper. Without this the fire endpoint refuses every realistic call, which
  // is why POST /kitchen/fire has never moved an item in production.
  // One-directional: fired never goes back to sentToKitchen.
  sentToKitchen: ['inPreparation', 'voided', 'ready', 'fired'],
  fired:         ['inPreparation', 'voided', 'ready'],
  inPreparation: ['ready', 'voided'],
  ready:         ['served', 'voided'],
  served:        ['voided'],  // Can void even after serving (food cost tracked)
  cancelled:     [],          // Terminal
  voided:        [],          // Terminal
} as const;

/**
 * Is this a move that finishes a line the kitchen never received (`pending` →
 * `ready` / `served`)? The kitchen orchestration refuses it with the typed
 * `ITEM_NOT_SENT_TO_KITCHEN` (D4), so every caller learns WHY, not just that
 * the transition was invalid.
 */
export function isUnsentLineFinish(from: KitchenStatus, to: KitchenStatus): boolean {
  return from === 'pending' && (to === 'ready' || to === 'served');
}

/** Terminal kitchen statuses — no further transitions allowed */
export const TERMINAL_KITCHEN_STATUSES: readonly KitchenStatus[] = ['cancelled', 'voided'] as const;

/**
 * Validate a per-item kitchen status transition.
 * @returns null if valid, error message if invalid
 */
export function validateKitchenStatusTransition(
  from: KitchenStatus,
  to: KitchenStatus,
): string | null {
  if (from === to) {
    return null; // Idempotent
  }

  const validTargets = KITCHEN_STATUS_TRANSITIONS[from];
  if (!validTargets) {
    return `Unknown kitchen status: "${from}"`;
  }

  if (!validTargets.includes(to)) {
    return `Invalid kitchen status transition: "${from}" → "${to}". Valid transitions from "${from}": [${validTargets.join(', ')}]`;
  }

  return null;
}

/**
 * The `voidReason` the functions cascade (`onOrderTerminalVoidChecks`) stamps
 * on every check it voids because its ORDER was voided. Restoring the order
 * restores exactly these checks — a check voided on its own before the order
 * was stays voided.
 */
export const PARENT_ORDER_VOIDED_REASON = 'Parent order voided';

/**
 * Validate taking an ORDER's void back: `voided` → `open`.
 *
 * Deliberately NOT an edge in {@link ORDER_STATE_TRANSITIONS}: `voided: []`
 * there is what stops every generic transition walking a void back by
 * accident. Restoring one is a single manager-approved act with its own route
 * and its own operation type (`restore_void`), the order-level twin of
 * {@link validateVoidRestore}. The order comes back OPEN and unpaid — the
 * void flow reversed its tenders, and nothing here re-charges them.
 *
 * @returns null if valid, error message if invalid
 */
export function validateOrderVoidRestore(from: OrderState): string | null {
  if (from !== 'voided') {
    return `Only a voided order can have its void removed; this order is "${from}"`;
  }
  return null;
}

/**
 * Validate taking a void BACK: `voided` → the status the line had before.
 *
 * Deliberately NOT an edge in {@link KITCHEN_STATUS_TRANSITIONS}. That table is
 * what every generic status update validates against — a KDS bump, the
 * delivery autopilot, `PATCH /kitchen/items` — and `voided: []` there is what
 * stops any of them walking a void back by accident. Reversing a void is one
 * deliberate, manager-approved act with its own route and its own operation
 * type (`restore_void`), so it gets its own rule instead of a hole in the
 * shared one.
 *
 * The target must be a status the kitchen had COMMITTED to. A line voided
 * before the kitchen saw it had its stock put back at void time
 * (`voidInfo.foodCostIncurred === false`), so it is re-rung, not restored.
 *
 * @returns null if valid, error message if invalid
 */
export function validateVoidRestore(
  from: KitchenStatus,
  to: KitchenStatus,
): string | null {
  if (from !== 'voided') {
    return `Only a voided line can have its void removed; this line is "${from}"`;
  }
  if (!KITCHEN_COMMITTED_STATUSES.includes(to)) {
    return `A void can only be removed back to a status the kitchen had (${KITCHEN_COMMITTED_STATUSES.join(', ')}); got "${to}"`;
  }
  return null;
}

/**
 * The statuses a counter HAND-OVER moves straight to `served`: every status the
 * kitchen has short of served — sent, fired, in preparation, Ready.
 */
export const HAND_OVER_FROM_STATUSES: readonly KitchenStatus[] = [
  'sentToKitchen',
  'fired',
  'inPreparation',
  'ready',
] as const;

/**
 * Validate a counter HAND-OVER: the guest has the food, so the line is
 * `served` — in ONE step, from any status the kitchen has short of served.
 *
 * Deliberately NOT an edge in {@link KITCHEN_STATUS_TRANSITIONS}. That table is
 * what a KDS bump validates against, and a bump must never skip Ready. Handing
 * a bag over the counter is a different act with its own route
 * (`POST /orders/:guid/kitchen/hand-over`) and its own operation type
 * (`hand_over`), so it gets its own rule instead of a hole in the shared one.
 *
 * Why one step at all: walking Ready then Served is TWO order writes, and the
 * Ready write is what queues the guest's "your order is ready" (and the staff
 * ORDER_READY banner) — sent while the guest is walking out with the bag. One
 * write goes `sent|preparing → served`, which the guest reads as "complete"
 * and nothing else.
 *
 * A line the kitchen never received (`pending`) is refused for every caller
 * (D4) — see {@link isUnsentLineFinish}; a served line is already handed over.
 *
 * @returns null if valid, error message if invalid
 */
export function validateHandOver(from: KitchenStatus): string | null {
  if (HAND_OVER_FROM_STATUSES.includes(from)) return null;
  if (from === 'served') {
    return 'This item has already been handed over';
  }
  return `Only an item the kitchen has can be handed over (${HAND_OVER_FROM_STATUSES.join(', ')}); this item is "${from}"`;
}

/**
 * Check if a kitchen status transition requires manager authorization.
 * Voiding items that have been committed to the kitchen requires approval.
 */
export function kitchenTransitionRequiresAuth(
  from: KitchenStatus,
  to: KitchenStatus,
): boolean {
  return to === 'voided' && from !== 'pending';
}

// ─── Kitchen State Machine (order-level) ────────────────────────────────────

/**
 * Valid transitions for order-level KitchenState.
 * Key = current state, Value = set of valid next states.
 */
export const KITCHEN_STATE_TRANSITIONS: Record<KitchenState, readonly KitchenState[]> = {
  none:      [],                                          // Non-kitchen orders stay 'none'
  pending:   ['sent', 'none'],                            // Can become 'none' if all items cancelled
  sent:      ['preparing', 'pending'],                    // Can go back if items recalled
  preparing: ['ready', 'sent'],                           // Can go back if items recalled
  ready:     ['served', 'preparing'],                     // Can go back if items need rework
  served:    [],                                          // Terminal for kitchen
} as const;

/**
 * Validate an order-level kitchen state transition.
 * @returns null if valid, error message if invalid
 */
export function validateKitchenStateTransition(
  from: KitchenState,
  to: KitchenState,
): string | null {
  if (from === to) {
    return null; // Idempotent
  }

  const validTargets = KITCHEN_STATE_TRANSITIONS[from];
  if (!validTargets) {
    return `Unknown kitchen state: "${from}"`;
  }

  if (!validTargets.includes(to)) {
    return `Invalid kitchen state transition: "${from}" → "${to}". Valid transitions from "${from}": [${validTargets.join(', ')}]`;
  }

  return null;
}

// ─── Payment Status Machine ─────────────────────────────────────────────────

/**
 * Valid transitions for PaymentStatus.
 * Key = current status, Value = set of valid next statuses.
 */
export const PAYMENT_STATUS_TRANSITIONS: Record<PaymentStatus, readonly PaymentStatus[]> = {
  unpaid:            ['processing', 'comped', 'failed'],
  processing:        ['paid', 'partiallyPaid', 'failed'],
  partiallyPaid:     ['processing', 'paid', 'refunded'],
  paid:              ['partiallyRefunded', 'refunded', 'voided'],
  partiallyRefunded: ['refunded'],
  refunded:          [],                                    // Terminal
  voided:            [],                                    // Terminal (payment void, not order void)
  failed:            ['processing', 'unpaid'],              // Retryable
  comped:            [],                                    // Terminal
} as const;

/** Terminal payment statuses — no further transitions allowed */
export const TERMINAL_PAYMENT_STATUSES: readonly PaymentStatus[] = ['refunded', 'voided', 'comped'] as const;

/**
 * Validate a payment status transition.
 * @returns null if valid, error message if invalid
 */
export function validatePaymentStatusTransition(
  from: PaymentStatus,
  to: PaymentStatus,
): string | null {
  if (from === to) {
    return null; // Idempotent
  }

  const validTargets = PAYMENT_STATUS_TRANSITIONS[from];
  if (!validTargets) {
    return `Unknown payment status: "${from}"`;
  }

  if (!validTargets.includes(to)) {
    return `Invalid payment status transition: "${from}" → "${to}". Valid transitions from "${from}": [${validTargets.join(', ')}]`;
  }

  return null;
}

// ─── Check State Machine (Axis 3) ───────────────────────────────────────────

/**
 * Valid transitions for CheckState (financial allocation lifecycle).
 *
 * open → finalized:    Payment begins. Items locked (no add/remove/reassign).
 * finalized → open:    Payment failed/cancelled. Items unlocked.
 * finalized → closed:  All payments settled (paymentStatus = paid). Terminal.
 * open → voided:       Manager voids check pre-payment. Items must be reassigned.
 * finalized → voided:  Manager voids mid-payment. All PaymentRecords must be voided/refunded first.
 */
export const CHECK_STATE_TRANSITIONS: Record<CheckState, readonly CheckState[]> = {
  // `open → closed` is REQUIRED, not a convenience. `finalized` is a
  // PRINT/LOCK concept (the bill was presented), not a payment one, and it is
  // set by a client. Making settlement reachable only from `finalized` meant a
  // client that unlocked the check between paying and the cascade landing
  // stranded a fully-paid check in `open` FOREVER — no later trigger revisits
  // it, the parent order never closes, and the sale never reaches Books.
  // A check with no balance owed is finished regardless of whether anyone
  // printed it. Payment-safety is enforced separately (see
  // `validateCheckPaymentSafety`) — this table governs shape only.
  open:        ['finalized', 'closed', 'voided', 'superseded'],
  finalized:   ['open', 'closed', 'voided'],  // open = unlock (payment cancelled/failed)
  closed:      [],  // Terminal
  voided:      [],  // Terminal
  superseded:  [],  // Terminal — replaced by split operation
} as const;

/** Terminal check states — no further transitions allowed */
export const TERMINAL_CHECK_STATES: readonly CheckState[] = ['closed', 'voided', 'superseded'] as const;

/**
 * Validate a check state transition.
 * @returns null if valid, error message if invalid
 */
export function validateCheckStateTransition(
  from: CheckState,
  to: CheckState,
): string | null {
  if (from === to) {
    return null; // Idempotent
  }

  const validTargets = CHECK_STATE_TRANSITIONS[from];
  if (!validTargets) {
    return `Unknown check state: "${from}"`;
  }

  if (!validTargets.includes(to)) {
    return `Invalid check state transition: "${from}" → "${to}". Valid transitions from "${from}": [${validTargets.join(', ')}]`;
  }

  return null;
}

/**
 * Check if voiding a check requires manager authorization.
 * Voiding a finalized check (payment in progress) always requires auth.
 */
export function checkVoidRequiresAuth(checkState: CheckState): boolean {
  return checkState === 'finalized';
}

/** A check reduced to just what a payment-safety decision needs. */
export interface CheckPaymentSnapshot {
  paidAmount?: number;
  paymentStatus?: string;
}

/** Payment statuses that mean real money is currently held against a check. */
const CHECK_HOLDS_MONEY_STATUSES = ['paid', 'partiallyPaid', 'paidPending'];

/** True iff [check] currently holds money that has not been reversed. */
export function checkHoldsMoney(check: CheckPaymentSnapshot): boolean {
  const byStatus =
    check.paymentStatus !== undefined &&
    CHECK_HOLDS_MONEY_STATUSES.includes(check.paymentStatus);
  return byStatus || (check.paidAmount ?? 0) > 0;
}

/**
 * Payment-safety gate for an ORDER state transition — the order-level twin of
 * {@link validateCheckPaymentSafety}.
 *
 * Voiding or cancelling a ticket is a REVENUE-side write-off. It does not, and
 * must not, reverse a tender: reversing money is a void/refund against the
 * PAYMENT, which carries its own authorization and audit trail. Those are two
 * different axes, and the order state machine only ever knew about one of them.
 *
 * So an order whose checks still hold un-reversed money cannot be written off.
 * Allowing it produces the worst outcome available: the ticket reads `voided`
 * (no revenue) while the money reads `approved` (collected), the check stays
 * `open`, and nothing anywhere reconciles the two. Production order #4 at Manje
 * Lakay (2026-08-04) is exactly that shape — `orderState: voided` over a live
 * `approved` $25.23 cash tender on an `open` check, with the drawer counting
 * over by that amount and Books showing nothing.
 *
 * The remedy the message names is the correct sequence: reverse the payment
 * first (void if unsettled, refund if settled), THEN write the ticket off.
 *
 * @param targetState the order state being transitioned to
 * @param checks payment snapshots of the order's ACTIVE checks
 * @returns null if permitted, an error message otherwise
 */
export function validateOrderPaymentSafety(
  targetState: OrderState,
  checks: readonly CheckPaymentSnapshot[],
): string | null {
  if (targetState !== 'voided' && targetState !== 'cancelled') return null;

  const funded = checks.filter(checkHoldsMoney);
  if (funded.length === 0) return null;

  const total = funded.reduce((sum, c) => sum + (c.paidAmount ?? 0), 0);
  return (
    `Cannot ${targetState === 'voided' ? 'void' : 'cancel'} this order — ` +
    `${funded.length} check(s) still hold payment` +
    (total > 0 ? ` totalling ${total.toFixed(2)}` : '') +
    `. Refund or void the payment first, then write the ticket off.`
  );
}

/**
 * Payment-safety gate for a check state transition — ORTHOGONAL to
 * {@link validateCheckStateTransition}, which governs shape only. It refuses two
 * targets while the check holds money ({@link checkHoldsMoney}, the one
 * definition the order-level gate uses too): `open` and `voided`.
 *
 * ── `→ voided` ──────────────────────────────────────────────────────────────
 * Voiding a check withdraws the BILL; it does not reverse a tender. A funded
 * check voided anyway reads `voided` over an `approved` payment — money
 * collected against a bill that no longer exists, which the payment cascade,
 * the drawer and Books each read differently and nothing reconciles. The remedy
 * is to void or refund the payment first (docs/PAYMENT_REVERSAL_ARCHITECTURE.md
 * §The rule), exactly as for an order write-off.
 *
 * ── `→ open` ────────────────────────────────────────────────────────────────
 * `finalized → open` ("unlock") means *the operator backed out before paying*.
 * It is the compensating action for a payment that never happened, and the
 * state machine alone cannot tell that apart from a payment that DID happen —
 * both leave the check `finalized`.
 *
 * That gap is not theoretical. The POS locks the check, takes the money, and
 * pops the payment screen; the payment cascade then needs ~2-3s to write
 * `paymentStatus: 'paid'`. An operator dismissing the screen faster than that
 * had the client issue an unlock against a check that was already paid, which
 * reverted `finalized → open` and permanently stranded a settled check (the
 * cascade closes on settlement, but nothing re-drives a check after the fact).
 * The order never closed, the sale never reached Books, and the ticket stayed
 * cancellable — one such order was VOIDED in production while holding $25.23
 * of collected cash.
 *
 * So money on the check REFUSES the unlock, server-side, unconditionally.
 * The client is not trusted to win a race it cannot observe. Reversing a real
 * payment is a void/refund (which carries an audit trail and an authorization),
 * never an unlock.
 *
 * @param targetState the state being transitioned to
 * @param paidAmount  money recorded against the check
 * @param paymentStatus the check's current payment status
 * @returns null if the transition is permitted, an error message otherwise
 */
export function validateCheckPaymentSafety(
  targetState: CheckState,
  paidAmount: number | undefined,
  paymentStatus: string | undefined,
): string | null {
  if (targetState !== 'open' && targetState !== 'voided') return null;

  // `paidPending` (offline capture awaiting replay) counts: the guest has
  // handed the money over. `partiallyPaid` counts: a split-tender in progress
  // still holds real money. `refunded`/`voided` do NOT — those are already
  // reversed, and re-opening or withdrawing such a check is a legitimate
  // correction flow. One definition for both gates: `checkHoldsMoney`.
  if (!checkHoldsMoney({ paidAmount, paymentStatus })) return null;

  const carries =
    `it carries a payment ` +
    `(paidAmount: ${paidAmount ?? 0}, paymentStatus: "${paymentStatus ?? 'unknown'}")`;
  return targetState === 'voided'
    ? `Cannot void check — ${carries}. Void or refund the payment first, then void the check.`
    : `Cannot unlock check — ${carries}. Reverse the payment with a void or refund instead.`;
}

// ─── Aggregate Kitchen State Deriver ────────────────────────────────────────

/**
 * Derive order-level KitchenState from the aggregate of per-item KitchenStatus values.
 *
 * Rules (evaluated in priority order):
 * 1. If enableKitchenNotifications is false → 'none'
 * 2. If all active items are served → 'served'
 * 3. If any item is 'ready' and none are 'inPreparation' or 'sentToKitchen' → 'ready'
 * 4. If any item is 'inPreparation' → 'preparing'
 * 5. If any item is 'sentToKitchen' or 'fired' → 'sent'
 * 6. If any item is 'pending' → 'pending'
 * 7. If no active items remain → 'none' (all cancelled/voided)
 *
 * Active items = items not in terminal state (cancelled, voided)
 */
export function deriveKitchenState(
  itemStatuses: KitchenStatus[],
  enableKitchenNotifications: boolean = true,
): KitchenState {
  if (!enableKitchenNotifications) {
    return 'none';
  }

  // Filter to active items only (not cancelled or voided)
  const activeStatuses = itemStatuses.filter(
    s => s !== 'cancelled' && s !== 'voided',
  );

  if (activeStatuses.length === 0) {
    return 'none';
  }

  // Check from most advanced to least advanced
  const allServed = activeStatuses.every(s => s === 'served');
  if (allServed) return 'served';

  const hasInPreparation = activeStatuses.includes('inPreparation');
  const hasSentOrFired = activeStatuses.includes('sentToKitchen') || activeStatuses.includes('fired');
  const hasReady = activeStatuses.includes('ready');

  if (hasReady && !hasInPreparation && !hasSentOrFired) return 'ready';
  if (hasInPreparation) return 'preparing';
  if (hasSentOrFired) return 'sent';

  // All remaining items must be 'pending'
  return 'pending';
}
